/**
 * GamificationChrome — Common wrapper for all gamification type renderers
 *
 * Renders shared UI elements around the type-specific content:
 * - Title + description
 * - Sponsor branding (if brandActivationId)
 * - Timer (countdown to endAt, ticks every minute)
 * - Milestone progress bar
 * - Social proof (participant count, completion count)
 * - Share button (copies current URL to clipboard)
 */
import React, {useMemo, useState, useEffect} from "react";
import toast from "react-hot-toast";
import {GamificationModelResponse} from "@/hooks/gamification/useGetGamificationModel";
import {GamificationDisplayMode} from "./rendererRegistry";
import "./GamificationChrome.scss";

interface GamificationChromeProps {
    model: GamificationModelResponse;
    displayMode: GamificationDisplayMode;
    children: React.ReactNode;
    sectionTitle?: string;
}

/** Types whose renderer draws a complete card — see the bare branch below. */
const SELF_FRAMED_TYPES: ReadonlySet<string> = new Set(["STREAK_BOARD"]);

function formatTimeRemaining(endAt: number): string {
    const diff = endAt - Date.now();
    if (diff <= 0) return "Ended";

    const days = Math.floor(diff / 86400000);
    const hours = Math.floor(diff % 86400000 / 3600000);
    const minutes = Math.floor(diff % 3600000 / 60000);

    if (days > 0) return `${days}d ${hours}h remaining`;
    if (hours > 0) return `${hours}h ${minutes}m remaining`;
    return `${minutes}m remaining`;
}

export const GamificationChrome = ({model, displayMode, children, sectionTitle}: GamificationChromeProps) => {
    // Ticking timer — re-evaluates once per minute while the model is active
    const [timerStr, setTimerStr] = useState(() =>
        model.endAt && model.status === "ACTIVE" ? formatTimeRemaining(model.endAt) : "");

    useEffect(() => {
        if (!model.endAt || model.status !== "ACTIVE") return;
        const interval = setInterval(() => {
            setTimerStr(formatTimeRemaining(model.endAt!));
        }, 60000); // 1-minute cadence matches formatTimeRemaining's minimum precision
        return () => clearInterval(interval);
    }, [model.endAt, model.status]);

    const milestoneProgress = useMemo(() => {
        if (!model.milestones || model.milestones.length === 0) return null;
        const reached = model.milestones.filter(m => m.reached).length;
        return {reached, total: model.milestones.length, pct: Math.round(reached / model.milestones.length * 100)};
    }, [model.milestones]);

    function handleShare(): void {
        navigator.clipboard.writeText(window.location.href).then(() => {
            toast.success("Link copied!");
        });
    }

    if (displayMode === "micro") {
        return (
            <div className="gamification-chrome gamification-chrome--micro">
                <span className="gamification-chrome__name">{sectionTitle || model.name}</span>
                {milestoneProgress &&
                    <span className="gamification-chrome__micro-progress">
                        {milestoneProgress.reached}/{milestoneProgress.total}
                    </span>
                }
                {children}
            </div>
        );
    }

    const isCompact = displayMode === "compact";

    /*
     * SOME TYPES DRAW THEIR OWN COMPLETE CARD.
     *
     * The anchor's streak card carries its own chip, day count, number, week
     * and copy line inside one bordered box. Wrapping that in the generic
     * chrome duplicates every part of it — the name appears twice, the
     * description twice, and a milestone bar and participant count appear that
     * the anchor deliberately has none of: "a streak is ONE number and a mark;
     * the moment it becomes 5-of-7 with a row of bars the reader is decoding a
     * fraction and the whole advantage is gone."
     *
     * So at full size these render bare. Compact and micro still take the
     * chrome, because there the card is a fragment rather than a card.
     */
    if (SELF_FRAMED_TYPES.has(model.type) && !isCompact) {
        return <div className="gamification-chrome gamification-chrome--bare">{children}</div>;
    }

    return (
        <div className={`gamification-chrome gamification-chrome--${displayMode}`}>
            {sectionTitle &&
                <p className="gamification-chrome__section-title">{sectionTitle}</p>
            }
            <div className="gamification-chrome__header">
                <h3 className="gamification-chrome__title">{model.name}</h3>
                <div className="gamification-chrome__header-actions">
                    {timerStr &&
                        <span className="gamification-chrome__timer">{timerStr}</span>
                    }
                    {!isCompact &&
                        <button
                            type="button"
                            className="gamification-chrome__share-btn"
                            onClick={handleShare}
                            aria-label="Share"
                        >
                            Share
                        </button>
                    }
                </div>
            </div>

            {!isCompact && model.description &&
                <p className="gamification-chrome__description">{model.description}</p>
            }

            {milestoneProgress &&
                <div className="gamification-chrome__milestones">
                    <div className="gamification-chrome__milestone-bar">
                        <div
                            className="gamification-chrome__milestone-fill"
                            style={{width: `${milestoneProgress.pct}%`}}
                        />
                    </div>
                    {!isCompact &&
                        <span className="gamification-chrome__milestone-label">
                            {milestoneProgress.reached} of {milestoneProgress.total} milestones
                        </span>
                    }
                </div>
            }

            <div className="gamification-chrome__content">
                {children}
            </div>

            <div className="gamification-chrome__footer">
                <div className="gamification-chrome__social-proof">
                    {model.participantCount > 0 &&
                        <span className="gamification-chrome__participants">
                            {model.participantCount.toLocaleString()} {isCompact ? "" : "participants"}
                        </span>
                    }
                    {model.completionCount > 0 &&
                        <span className="gamification-chrome__completions">
                            {model.completionCount.toLocaleString()} completed
                        </span>
                    }
                </div>
                {model.status === "COMPLETED" &&
                    <span className="gamification-chrome__status-badge">Completed</span>
                }
            </div>
        </div>
    );
};
